import { useEffect, useRef, useState, useCallback } from "react";
import axios from "axios";

import { API, BACKEND_URL } from '../utils/apiBase';

/**
 * Establishes a WebSocket connection to the timer channel for an event.
 * Caches battle/circle timer states per ID and exposes a setter to consumer.
 *
 * @param {string} eventId - the event_id to subscribe to.
 * @returns {{ battleTimers, circleTimers, settings, refreshBattle, refreshCircle, refreshSettings }}
 */
export default function useTimerWS(eventId) {
  const [battleTimers, setBattleTimers] = useState({});
  const [circleTimers, setCircleTimers] = useState({});
  const [settings, setSettings] = useState({ countdown_mode: false, circle_position_order: "4to1", countdown_sound_enabled: false });
  const wsRef = useRef(null);
  const reconnectTimer = useRef(null);
  // Track IDs we've seen so we can rehydrate their state after a missed WS
  // message (e.g. when an OBS browser source returns from background, or
  // a Samsung tablet drops/regains wifi).
  const knownBattleIdsRef = useRef(new Set());
  const knownCircleIdsRef = useRef(new Set());

  const refreshSettings = useCallback(async () => {
    if (!eventId) return;
    try {
      const res = await axios.get(`${API}/event/${eventId}/countdown-settings`);
      setSettings(res.data);
    } catch { /* keep last known */ }
  }, [eventId]);

  const refreshBattle = useCallback(async (battleId) => {
    if (!battleId) return;
    try {
      knownBattleIdsRef.current.add(battleId);
      const res = await axios.get(`${API}/battle/${battleId}/timer`);
      setBattleTimers(prev => ({ ...prev, [battleId]: res.data }));
    } catch { /* keep last known */ }
  }, []);

  const refreshCircle = useCallback(async (circleId) => {
    if (!circleId) return;
    try {
      knownCircleIdsRef.current.add(circleId);
      const res = await axios.get(`${API}/circle/${circleId}/timer`);
      setCircleTimers(prev => ({ ...prev, [circleId]: res.data }));
    } catch { /* keep last known */ }
  }, []);

  // Force-refresh ALL known timer entities. Safety net for missed WS frames
  // (background tab, wifi drop, OBS browser source returning to scene, ...).
  const rehydrateAll = useCallback(() => {
    refreshSettings();
    knownBattleIdsRef.current.forEach((id) => { refreshBattle(id); });
    knownCircleIdsRef.current.forEach((id) => { refreshCircle(id); });
  }, [refreshSettings, refreshBattle, refreshCircle]);

  // Auto-rehydrate on visibility regain and on network reconnect.
  useEffect(() => {
    const onVisible = () => {
      if (document.visibilityState === 'visible') rehydrateAll();
    };
    const onOnline = () => { rehydrateAll(); };
    document.addEventListener('visibilitychange', onVisible);
    window.addEventListener('online', onOnline);
    window.addEventListener('focus', onVisible);
    return () => {
      document.removeEventListener('visibilitychange', onVisible);
      window.removeEventListener('online', onOnline);
      window.removeEventListener('focus', onVisible);
    };
  }, [rehydrateAll]);

  useEffect(() => {
    if (!eventId) return;
    refreshSettings();

    // Prefer same-origin WebSocket when running in a browser. This is the
    // ONE thing that lets custom domains (e.g. judging.worldfreestylefootball.org)
    // reach the backend WS: if we hard-code REACT_APP_BACKEND_URL, WS becomes
    // cross-origin and browsers/ingresses will silently drop the upgrade.
    // We fall back to REACT_APP_BACKEND_URL only in non-browser contexts.
    let wsBase;
    if (typeof window !== "undefined" && window.location) {
      const proto = window.location.protocol === "https:" ? "wss:" : "ws:";
      wsBase = `${proto}//${window.location.host}`;
    } else {
      wsBase = BACKEND_URL.replace(/^http/, "ws");
    }
    const url = `${wsBase}/api/ws/timer/${eventId}`;

    let cancelled = false;
    const connect = () => {
      if (cancelled) return;
      try {
        const ws = new WebSocket(url);
        wsRef.current = ws;
        ws.onopen = () => {
          // On (re)connect, immediately re-sync every known timer entity
          // so we recover from any missed message during the WS downtime.
          // This is the fix for "stop → relance met longtemps à revenir":
          // if the WS dropped between STOP and RESTART, this rehydrates in
          // one shot as soon as the socket comes back.
          rehydrateAll();
        };
        ws.onmessage = (ev) => {
          try {
            const msg = JSON.parse(ev.data);
            if (msg.type === "battle_timer" && msg.battle_id) {
              setBattleTimers(prev => ({ ...prev, [msg.battle_id]: msg.timer }));
            } else if (msg.type === "circle_timer" && msg.circle_id) {
              setCircleTimers(prev => ({ ...prev, [msg.circle_id]: msg.timer }));
            } else if (msg.type === "countdown_settings") {
              setSettings(prev => ({ ...prev, ...msg.settings }));
            }
          } catch {}
        };
        ws.onclose = () => {
          if (cancelled) return;
          reconnectTimer.current = setTimeout(connect, 2000);
        };
        ws.onerror = () => {
          try { ws.close(); } catch {}
        };
      } catch {
        reconnectTimer.current = setTimeout(connect, 2000);
      }
    };
    connect();

    return () => {
      cancelled = true;
      if (reconnectTimer.current) clearTimeout(reconnectTimer.current);
      if (wsRef.current) {
        try { wsRef.current.close(); } catch {}
      }
    };
  }, [eventId, refreshSettings, rehydrateAll]);

  return { battleTimers, circleTimers, settings, refreshBattle, refreshCircle, refreshSettings };
}
